import {
  MainPropertyType,
  ORDER_FEATURES,
  OrderFeature,
  formatPropertyType,
  getDefaultStringifiedFeature,
  isDefined,
  orUndefined,
  schImprovements,
  schMainPropertyType,
  syntaxFNMA,
  termsOfSaleOptions,
} from '@aloft/toolkit-shared';
import { Block, Flex, IconButton, SimpleInput, SimpleSelect, Typography } from '@aloft/toolkit-sui';

import { PropsWithChildren, ReactNode, useState } from 'react';
import { Controller } from 'react-hook-form';

import { useZodFormContext } from '../../../../hooks/useZodForm.ts';
import { FEATURES, Feature } from '../../../../redux/constants.ts';
import {
  selectFormDisclosureState,
  selectImprovementsById,
} from '../../../../redux/features/selectors/propertyDataSelectors/improvementsSelectors.ts';
import { useAppSelector } from '../../../../redux/hooks.ts';
import { RootState } from '../../../../redux/types.ts';
import { Divider } from '../../../../components/Analysis/ColumnComponents/Divider.tsx';
import { AreaInput } from '../../InputComponents/AreaInput.tsx';
import { AreaInputWithUnit } from '../../InputComponents/AreaInputWithUnit.tsx';
import { MonthDateInput } from '../../InputComponents/MonthDateInput.tsx';
import { NumberInput } from '../../InputComponents/NumberInput.tsx';
import { PriceInput } from '../../InputComponents/PriceInput.tsx';
import { TextInput } from '../../InputComponents/TextInput.tsx';
import { getFeatureInput } from '../../InputComponents/getFeatureInput.tsx';
import { schObjectiveCharacteristics } from '../../schObjectiveCharacteristics.ts';
import { AddFeaturesPopover } from './AddFeaturesPopover.tsx';
import { getCostQualityRange } from './getCostQualityRange.tsx';

type ObjectiveCharacteristicsProps = {
  id: string;
};

type FormFeature = Exclude<Feature, 'finishedBasement' | 'totalBasement' | 'belowGradeBath'>;

const SubSection = ({
  header,
  headerRight,
  children,
  showDivider = false,
}: PropsWithChildren<{ header: string; headerRight?: ReactNode; showDivider?: boolean }>) => (
  <Flex flexDirection="column" gap="1rem" marginBottom="1rem">
    {showDivider && <Divider />}
    <Flex alignItems="center" justifyContent="space-between">
      <Typography variant="h3">{header}</Typography>
      {isDefined(headerRight) && headerRight}
    </Flex>
    {children}
  </Flex>
);

const Section = ({ header, children }: PropsWithChildren<{ header: string }>) => (
  <Flex flexDirection="column" gap="1rem" marginBottom="1rem">
    <Block backgroundColor="surface4" padding="0.25rem" position="sticky" top={0}>
      <Typography variant="h2">{header}</Typography>
    </Block>
    <Flex flexDirection="column" gap="1rem" paddingX="0.5rem">
      {children}
    </Flex>
  </Flex>
);

export const ObjectiveCharacteristics = ({ id }: ObjectiveCharacteristicsProps) => {
  const initialDisclosureState = useAppSelector((state: RootState) =>
    selectFormDisclosureState(state, id),
  );
  const improvements = useAppSelector((state) => selectImprovementsById(state, id));
  const [propertyType, setPropertyType] = useState(improvements?.propertyType);
  const [disclosureState, setDisclosureState] = useState(initialDisclosureState);

  const { control, formState, register, setValue, trigger, watch } =
    useZodFormContext<typeof schObjectiveCharacteristics>();
  const { errors } = formState;

  const status = watch('status');

  const handleAddFeature = (feature: Feature) => {
    if (feature in ORDER_FEATURES && feature !== 'manualCost') {
      // eslint-disable-next-line no-param-reassign
      feature = feature as OrderFeature;

      const parsedImprovements = orUndefined(schImprovements.parse)(improvements);

      setValue(feature, getDefaultStringifiedFeature(feature, parsedImprovements), {
        shouldDirty: true,
        shouldValidate: true,
      });
    }

    setDisclosureState((prev) => [...prev, feature]);
  };

  const handleRemoveFeature = (feature: FormFeature) => {
    setValue(feature, undefined, { shouldDirty: true, shouldValidate: true });

    setDisclosureState((prev) => prev.filter((f) => f !== feature));
  };

  const showContractDate = status === 'Contract' || status === 'SettledSale';
  const showSaleDate = status === 'SettledSale';
  const showExpirationDate = status === 'Expired';
  const showWithdrawalDate = status === 'Withdrawn';

  if (!propertyType) throw new Error('Property type is required');

  const costQualityRange = getCostQualityRange(propertyType);

  const handlePropertyTypeChange = (newType: MainPropertyType) => {
    setPropertyType(newType);

    setValue('propertyType', newType, { shouldDirty: true, shouldValidate: true });
  }

  return (
    <Flex className="objective-edit-form" flexDirection="column" overflow="auto">
      <Section header="Property Features">
        <SubSection header="Main Dwelling">
          <Controller
            name="propertyType"
            control={control}
            render={({ field }) => (
              <SimpleSelect {...field} label="Property Type" size="sm" width="175px" onChange={(e) => handlePropertyTypeChange(e.target.value as MainPropertyType)} value={propertyType}>
                {schMainPropertyType.options.map((val) => ({ key: val, value: val, label: formatPropertyType(val) })).map((option) => (
                  <option key={option.key} value={option.value}>
                    {option.label ?? 'Single Family Residence'}
                  </option>
                ))}
              </SimpleSelect>
            )}
          />
          <Controller
            name="craftsmanQuality"
            control={control}
            render={({ field }) => (
              <NumberInput
                {...field}
                required
                min={costQualityRange.min}
                max={costQualityRange.max}
                precision={1}
                label="Cost Quality"
              />
            )}
          />
          {propertyType === 'manufacturedHome' && (
            <Controller
              name="foundation"
              control={control}
              render={({ field }) => (
                <SimpleSelect {...field} label="Foundation" size="sm" width="175px">
                  <option value="pierAndBeam">Pier and Beam</option>
                  <option value="slab">Slab</option>
                </SimpleSelect>
              )}
            />
          )}
          <Controller
            name="gla"
            control={control}
            render={({ field }) => (
              <AreaInput
                {...field}
                label={propertyType === 'multiFamily' ? 'GBA' : 'GLA'}
                required
                errorMessage={errors.gla?.message}
              />
            )}
          />
          {propertyType === 'multiFamily' && (
            <Controller
              name="numberOfUnits"
              control={control}
              render={({ field }) => (
                <NumberInput
                  {...field}
                  min={2}
                  max={4}
                  label="Number of Units"
                  value={field.value?.toString()}
                  errorMessage={errors.numberOfUnits?.message}
                />
              )}
            />
          )}
          <Controller
            name="fullBaths"
            control={control}
            render={({ field }) => (
              <NumberInput
                {...field}
                value={field.value?.toString()}
                label="Full Baths"
                errorMessage={errors.fullBaths?.message}
              />
            )}
          />

          <Controller
            name="halfBaths"
            control={control}
            render={({ field }) => (
              <NumberInput
                {...field}
                value={field.value?.toString()}
                label="Half Baths"
                errorMessage={errors.halfBaths?.message}
              />
            )}
          />

          <Controller
            name="uadQuality"
            control={control}
            render={({ field }) => (
              <SimpleInput
                size="sm"
                type="count"
                {...field}
                label="UAD Quality"
                min={1}
                max={6}
                prefix="Q"
                errorMessage={errors.uadQuality?.message}
              />
            )}
          />

          <Controller
            name="uadCondition"
            control={control}
            render={({ field }) => (
              <SimpleInput
                size="sm"
                type="count"
                {...field}
                label="UAD Condition"
                min={1}
                max={6}
                prefix="C"
                errorMessage={errors.uadCondition?.message}
              />
            )}
          />

          <Controller
            name="age"
            control={control}
            render={({ field }) => (
              <NumberInput
                {...field}
                value={field.value?.toString()}
                label="Actual Age"
                errorMessage={errors.age?.message}
              />
            )}
          />
        </SubSection>

        {disclosureState.map((feature) => (
          <SubSection
            key={feature}
            header={FEATURES[feature].label}
            headerRight={
              <IconButton
                icon="delete-bin"
                variant="tertiary"
                iconColor="textTertiary"
                size="sm"
                onClick={() => handleRemoveFeature(feature as FormFeature)}
                ariaLabel={`Remove ${feature}`}
              />
            }
            showDivider
          >
            {getFeatureInput(register, trigger, errors, control, feature as FormFeature)}
          </SubSection>
        ))}
        <Flex alignSelf="flex-start">
          <AddFeaturesPopover
            features={disclosureState}
            label="+ Add Feature"
            handleAddFeature={handleAddFeature}
          />
        </Flex>
      </Section>
      <Section header="Site">
        <Block className="area-with-unit">
          <Controller
            name="area.area"
            control={control}
            render={({ field }) => (
              <AreaInputWithUnit {...field} label="Site size" unitControlName="area.unit" />
            )}
          />
        </Block>
        <Controller
          name="location"
          control={control}
          render={({ field }) => <TextInput {...field} label="Location" />}
        />
        <Controller
          name="view"
          control={control}
          render={({ field }) => <TextInput {...field} label="View" />}
        />
        <Block className="full-width">
          <Controller
            name="notes"
            control={control}
            render={({ field }) => <TextInput {...field} label="Site Notes (opt)" />}
          />
        </Block>
      </Section>

      <Section header="Transaction Details">
        <Controller
          name="price"
          control={control}
          render={({ field }) => <PriceInput {...field} label="Price" />}
        />
        {id !== 'subject' && (
          <>
            <Controller
              name="originalListPrice"
              control={control}
              render={({ field }) => <PriceInput {...field} label="Orig. List Price" />}
            />
            <Controller
              name="daysOnMarket"
              control={control}
              render={({ field }) => (
                <NumberInput {...field} value={field.value?.toString()} label="Days on Market" />
              )}
            />
            <SimpleSelect
              {...register('status')}
              onChange={(event) => {
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                register('status').onChange(event);
                // eslint-disable-next-line @typescript-eslint/no-floating-promises
                trigger(['saleDate', 'contractDate', 'withdrawalDate', 'expirationDate']);
              }}
              label="Status"
              size="sm"
              width="175px"
            >
              <option value="">None</option>
              {syntaxFNMA.valuesUadListingStatus
                .map((val) => ({ key: val, value: val, label: val }))
                .map((option) => (
                  <option key={option.key} value={option.value}>
                    {option.label}
                  </option>
                ))}
            </SimpleSelect>
            {showContractDate && (
              <Controller
                name="contractDate"
                control={control}
                render={({ field }) => (
                  <MonthDateInput
                    {...field}
                    label="Contract Date"
                    required={status === 'Contract'}
                    errorMessage={errors.contractDate?.message}
                  />
                )}
              />
            )}
            {showSaleDate && (
              <Controller
                name="saleDate"
                control={control}
                render={({ field }) => (
                  <MonthDateInput
                    {...field}
                    label="Sale Date"
                    required
                    errorMessage={errors.saleDate?.message}
                  />
                )}
              />
            )}
            {showExpirationDate && (
              <Controller
                name="expirationDate"
                control={control}
                render={({ field }) => (
                  <MonthDateInput
                    {...field}
                    label="Expiration Date"
                    required
                    errorMessage={errors.expirationDate?.message}
                  />
                )}
              />
            )}
            {showWithdrawalDate && (
              <Controller
                name="withdrawalDate"
                control={control}
                render={({ field }) => (
                  <MonthDateInput
                    {...field}
                    label="Withdrawal Date"
                    required
                    errorMessage={errors.withdrawalDate?.message}
                  />
                )}
              />
            )}
            <SimpleSelect
              {...register('termsOfSale')}
              label="Terms of Sale"
              size="sm"
              width="175px"
            >
              <option value="">None</option>
              {termsOfSaleOptions.map((option) => (
                <option key={option.key} value={option.value}>
                  {option.label}
                </option>
              ))}
            </SimpleSelect>

            <Controller
              name="concessions"
              control={control}
              render={({ field }) => <TextInput {...field} label="Concessions" />}
            />
          </>
        )}
      </Section>
    </Flex>
  );
};
